import React, {useEffect, useMemo, useCallback} from 'react'
import styled from 'styled-components'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Button from '@mui/material/Button'
import pdfIcon from '../../../assets/pdf.png'
import docxIcon from '../../../assets/docx.png'
import corret from '../../../assets/correctAnalysis.svg'
import aiIcon from '../../../assets/aiIcon.svg'
import warning from '../../../assets/warningAnalysis.svg'
import error from '../../../assets/errorAnalysis.svg'
import empty from '../../../assets/projeto-vazio.svg'
import {
    Alert,
    Box,
    CircularProgress,
    LinearProgress,
    Modal,
    Pagination,
    Skeleton,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TextField,
} from '@mui/material'
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import {AnalyzerApiService} from '../../../services/AnalyzerApi.service'
import {AssistantApi} from '../../../services/AssistantApi.service'
import {DocAnalysisResponse} from 'types/docAnalyses'
import {AnalyzedItemResponse} from 'types/analyzedItens'
import {HistoryContext} from '../../../contexts/history'
import Snackbar, {
    SnackbarCloseReason,
    SnackbarOrigin,
} from '@mui/material/Snackbar'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import InfoIcon from '@mui/icons-material/Info'
import {getCurrentColors} from '../../../utils/colors'
import {generateAnalysisPdf} from '../../../components/AnalysisPdf'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
    DOC_TYPES,
    DocType,
    DocumentTypeBadge,
    Regime,
} from '../../../constants/documentTypes'

const colors = getCurrentColors()
const MAX_FILE_SIZE_MB = 20
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

interface RegimeOption {
    value: Regime
    lei: string
    label: string
    sublabel: string
    desc: string
    accent: string
    soft: string
}

const REGIMES: RegimeOption[] = [
    {
        value: '14133',
        lei: 'Lei nº 14.133/2021',
        label: 'Lei Geral de Licitações',
        sublabel: 'Administração Pública',
        desc: 'Órgãos e entidades da Administração Pública direta, autárquica e fundacional.',
        accent: '#050B29',
        soft: 'rgba(5,11,41,0.06)',
    },
    {
        value: '13303',
        lei: 'Lei nº 13.303/2016',
        label: 'Lei das Estatais',
        sublabel: 'Empresas Estatais',
        desc: 'Empresas públicas e sociedades de economia mista, regidas pela Lei nº 13.303/2016.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
    },
]

interface State extends SnackbarOrigin {
    open: boolean
}

const Section = styled.section`
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    background-color: #f6f8fa;
    padding: 28px 40px;
    overflow-y: auto;
`
const Body = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 40px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const Title = styled.span`
    margin-top: 40px;
    font-size: 24px;
    text-align: start;
    top: 58px;
`

const AnalyzedItemContent = styled.div`
    font-size: 18px;
    color: #5e5e5e;

    p {
        margin: 0 0 8px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }
`

export default function DocumentAnalyzer() {
    const [file, setFile] = React.useState<File | null>(null)
    const [isLoading, setIsLoading] = React.useState(false)
    const [progress, setProgress] = React.useState(0)
    const [loadingMessage, setLoadingMessage] =
        React.useState('Lendo os textos')
    const analyzerApi = useMemo(() => new AnalyzerApiService(), [])
    const api = useMemo(() => new AnalyzerApiService(), [])
    const [DocAnalysisList, setDocAnalysisList] =
        React.useState<DocAnalysisResponse>([])
    const [docIdsList, setDocIdsList] = React.useState<string[]>([])
    const [analyzedItems, setAnalyzedItems] = React.useState<
        AnalyzedItemResponse[]
    >([])
    const [detailsLoading, setDetailsLoading] = React.useState<
        Record<number, boolean>
    >({})
    const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0)
    const [uploadPhase, setUploadPhase] = React.useState<
        'idle' | 'uploading' | 'analyzing'
    >('idle')

    const {redSocketMessage, setRedSocketMessage, getProjects} =
        React.useContext(HistoryContext)

    const [loadingList, setLoadingList] = React.useState(true)
    const [selectedDocType, setSelectedDocType] = React.useState<string>('')
    const [regime, setRegime] = React.useState<Regime>('14133')

    const visibleDocumentTypes = React.useMemo<DocType[]>(() => {
        if (!docIdsList.length) {
            return DOC_TYPES.filter((doc) => doc.apiId && !doc.regime)
        }
        const availableIds = new Set(docIdsList)
        return DOC_TYPES.filter(
            (doc) => doc.apiId && availableIds.has(doc.apiId)
        )
    }, [docIdsList])

    const selectedRegime = React.useMemo(
        () => REGIMES.find((r) => r.value === regime) ?? REGIMES[0],
        [regime]
    )

    const selectedDocumentType = React.useMemo(() => {
        return (
            visibleDocumentTypes.find(
                (item) => item.apiId === selectedDocType
            ) ||
            visibleDocumentTypes[0] ||
            null
        )
    }, [selectedDocType, visibleDocumentTypes])

    const selectedDocBg = React.useMemo(() => {
        if (selectedDocumentType) return selectedDocumentType.soft
        return '#f5f5f5'
    }, [selectedDocumentType])

    React.useEffect(() => {
        if (visibleDocumentTypes.length === 0) return

        const hasSelectedType = visibleDocumentTypes.some(
            (item) => item.apiId === selectedDocType
        )

        if (!hasSelectedType) {
            setSelectedDocType(visibleDocumentTypes[0].apiId)
        }
    }, [visibleDocumentTypes, selectedDocType])

    const [page, setPage] = React.useState(1)
    const [totalPages, setTotalPages] = React.useState(1)
    const [Toast, setToast] = React.useState<State>({
        open: true,
        vertical: 'top',
        horizontal: 'center',
    })
    const [pdfAlertMessage, setPdfAlertMessage] = React.useState('')
    const [modal, setModal] = React.useState(false)
    const [documentToDelete, setDocumentToDelete] = React.useState<
        string | null
    >(null)
    const [showAlertFileSize, setShowAlertFileSize] = React.useState(false)
    const [feedbackModal, setFeedbackModal] = React.useState(false)
    const [feedbackTarget, setFeedbackTarget] = React.useState<{
        uuid: string
        name: string
    } | null>(null)
    const [feedbackText, setFeedbackText] = React.useState('')
    const [feedbackSubmitting, setFeedbackSubmitting] = React.useState(false)
    const [feedbackToast, setFeedbackToast] = React.useState<{
        open: boolean
        message: string
        severity: 'success' | 'error'
    }>({open: false, message: '', severity: 'success'})

    const validateFileSize = useCallback((selectedFile: File) => {
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setShowAlertFileSize(true)
            return false
        }

        setShowAlertFileSize(false)

        return true
    }, [])

    const handleCloseToast = (
        _event: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason
    ) => {
        if (reason === 'clickaway') {
            return
        }

        setToast((prev) => ({...prev, open: false}))
    }

    const resetRedSocketMessage = useCallback(() => {
        setRedSocketMessage({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    }, [setRedSocketMessage])

    const loadAnalyses = useCallback(async () => {
        setLoadingList(true)
        console.log('Carregando análises de documentos, página:', page)
        const res = await api.listDocumentAnalyses(4, (page - 1) * 4)
        setLoadingList(false)
        setTotalPages(Math.ceil(res.total / 4))
        return res.documents
    }, [api, page])

    const refreshAnalyses = useCallback(async () => {
        try {
            const res = await loadAnalyses()
            setAnalyzedItems([])
            setDocAnalysisList(res)
            setExpandedIndex(0)
        } catch (error) {
            console.error('Erro ao carregar análises de documentos:', error)
        }
    }, [loadAnalyses])

    const fetchAnalyzedItems = useCallback(
        async (
            docAnalysisUuid: string,
            index: number
        ): Promise<AnalyzedItemResponse> => {
            setDetailsLoading((prev) => ({...prev, [index]: true}))
            try {
                const analyzedResponse =
                    await api.listAnalyzedItems(docAnalysisUuid)
                const items = analyzedResponse.items || []
                setAnalyzedItems((prev) => {
                    const next = [...prev]
                    next[index] = items
                    return next
                })
                return items
            } catch (error) {
                console.error('Erro ao carregar detalhes do documento:', error)
                setAnalyzedItems((prev) => {
                    const next = [...prev]
                    next[index] = []
                    return next
                })
                return []
            } finally {
                setDetailsLoading((prev) => ({...prev, [index]: false}))
            }
        },
        [api]
    )

    const handleAccordionChange = useCallback(
        (index: number, _docAnalysisUuid: string) =>
            (_event: React.SyntheticEvent, isExpanded: boolean) => {
                setExpandedIndex(isExpanded ? index : null)
            },
        []
    )

    const handleDownloadAnalysisPDF = useCallback(
        async (
            event: React.MouseEvent<HTMLButtonElement>,
            index: number,
            docAnalysisUuid: string,
            documentName: string
        ) => {
            event.stopPropagation()
            setPdfAlertMessage('')

            if (detailsLoading[index]) {
                return
            }

            let itemsToDownload = analyzedItems[index]

            if (!itemsToDownload) {
                itemsToDownload = await fetchAnalyzedItems(
                    docAnalysisUuid,
                    index
                )
            }

            if (!itemsToDownload || itemsToDownload.length === 0) {
                setPdfAlertMessage(
                    'A análise ainda não possui itens carregados para exportação em PDF.'
                )
                return
            }

            await generateAnalysisPdf(itemsToDownload, documentName)
        },
        [analyzedItems, detailsLoading, fetchAnalyzedItems]
    )

    const getFileExtension = useCallback((fileNameOrUrl?: string | null) => {
        if (!fileNameOrUrl) return ''

        const sanitizedValue = fileNameOrUrl.split('?')[0].toLowerCase()
        const extension = sanitizedValue.split('.').pop()

        return extension ?? ''
    }, [])

    const getFileIcon = useCallback(
        (fileNameOrUrl?: string | null) => {
            const extension = getFileExtension(fileNameOrUrl)

            if (extension === 'docx') {
                return docxIcon
            }

            return pdfIcon
        },
        [getFileExtension]
    )

    const getStatusVisuals = useCallback((status: number) => {
        switch (status) {
            case 0:
                return {
                    icon: corret,
                    label: 'Pontos Positivos',
                    color: '#2e7d32',
                }
            case 1:
                return {
                    icon: warning,
                    label: 'Pontos de Alerta',
                    color: '#ed6c02',
                }
            case 2:
                return {icon: error, label: 'Pontos de erro', color: '#d32f2f'}
            default:
                return {icon: aiIcon, label: 'Info', color: '#1976d2'}
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const fetchDocumentTypeIds = async () => {
            try {
                const res = await api.documentsTypesIds()
                if (isMounted) {
                    setDocIdsList(res)
                }
            } catch (error) {
                console.error('Erro ao carregar tipos de documento:', error)
            }
        }

        fetchDocumentTypeIds()

        return () => {
            isMounted = false
        }
    }, [api])

    useEffect(() => {
        let isMounted = true

        const fetchAnalyses = async () => {
            try {
                const res = await loadAnalyses()
                if (isMounted) {
                    setDocAnalysisList(res)
                }
            } catch (error) {
                console.error('Erro ao carregar análises de documentos:', error)
            }
        }

        fetchAnalyses()

        return () => {
            isMounted = false
        }
    }, [loadAnalyses])

    React.useEffect(() => {
        let timer: NodeJS.Timeout
        if (isLoading) {
            timer = setInterval(() => {
                setProgress((prevProgress) =>
                    prevProgress >= 100 ? 100 : prevProgress + 10
                )
            }, 500)
        }
        return () => {
            clearInterval(timer)
        }
    }, [isLoading])

    useEffect(() => {
        if (
            expandedIndex !== null &&
            DocAnalysisList[expandedIndex] &&
            !analyzedItems[expandedIndex]
        ) {
            fetchAnalyzedItems(
                DocAnalysisList[expandedIndex].doc_analysis_uuid,
                expandedIndex
            )
        }
    }, [DocAnalysisList, expandedIndex, analyzedItems, fetchAnalyzedItems])

    useEffect(() => {
        if (!redSocketMessage?.status) {
            return
        }

        console.log('Red Socket Message received:', redSocketMessage)

        const normalizedStatus = redSocketMessage.status.toUpperCase()

        if (normalizedStatus === 'UPLOADED' && uploadPhase !== 'idle') {
            setUploadPhase('analyzing')
            setLoadingMessage('Arquivo enviado. Analisando documento...')
            resetRedSocketMessage()
            return
        }

        if (normalizedStatus === 'FINISHED') {
            if (uploadPhase !== 'idle') {
                setLoadingMessage('Análise concluída. Atualizando lista...')
                setProgress(100)
                setIsLoading(false)
                setUploadPhase('idle')
                setFile(null)
                setTimeout(() => setProgress(0), 300)
            }

            refreshAnalyses()
            getProjects()
            resetRedSocketMessage()
        }
    }, [
        redSocketMessage,
        uploadPhase,
        resetRedSocketMessage,
        refreshAnalyses,
        getProjects,
    ])

    const handleAnalyzeProject = useCallback(
        async (selectedFile: File) => {
            if (!validateFileSize(selectedFile)) {
                return
            }

            if (!selectedDocumentType?.apiId) {
                setPdfAlertMessage(
                    regime === '13303'
                        ? 'A análise pela Lei das Estatais (Lei nº 13.303/2016) ainda está sendo configurada para este tipo de documento. Em breve disponível.'
                        : 'Selecione um tipo de documento válido antes de enviar.'
                )
                return
            }

            setFile(selectedFile)
            setIsLoading(true)
            setProgress(0)
            setUploadPhase('uploading')
            setLoadingMessage('Enviando documento...')
            resetRedSocketMessage()

            try {
                await analyzerApi.analyzeProject(
                    selectedFile,
                    selectedDocumentType.apiId,
                    regime
                )
                setLoadingMessage(
                    'Arquivo enviado. Aguardando confirmação do servidor...'
                )
            } catch (error) {
                console.error('Erro ao enviar arquivo para análise:', error)
                setLoadingMessage('Erro ao enviar arquivo. Tente novamente.')
                setIsLoading(false)
                setUploadPhase('idle')
                return
            }
        },
        [
            analyzerApi,
            resetRedSocketMessage,
            selectedDocType,
            selectedDocumentType,
            validateFileSize,
            regime,
        ]
    )

    const handleCloseModal = () => {
        setModal(false)
        setDocumentToDelete(null)
    }

    const handleOpenFeedbackModal = (uuid: string, name: string) => {
        setFeedbackTarget({uuid, name})
        setFeedbackText('')
        setFeedbackModal(true)
    }

    const handleCloseFeedbackModal = () => {
        setFeedbackModal(false)
        setFeedbackTarget(null)
        setFeedbackText('')
    }

    const handleSubmitFeedback = async () => {
        if (!feedbackTarget || !feedbackText.trim()) return

        setFeedbackSubmitting(true)
        try {
            const assistantApi = new AssistantApi()
            await assistantApi.reportModule({
                object_id: feedbackTarget.uuid,
                module: 'document_analyzer',
                title: feedbackTarget.name,
                feedback: feedbackText,
            })
            setFeedbackToast({
                open: true,
                message: 'Obrigado pelo feedback.',
                severity: 'success',
            })
            handleCloseFeedbackModal()
        } catch (error) {
            console.error('Erro ao enviar feedback:', error)
            setFeedbackToast({
                open: true,
                message: 'Erro ao enviar feedback. Tente novamente.',
                severity: 'error',
            })
        } finally {
            setFeedbackSubmitting(false)
        }
    }

    const handleConfirmModal = () => {
        if (!documentToDelete) return

        api.deleteDocumentAnalysis(documentToDelete)
            .then(() => {
                getProjects()
                refreshAnalyses()
                console.log('Documento apagado com sucesso')
            })
            .catch((error) => {
                console.error('Erro ao apagar documento:', error)
            })
        setModal(false)
        setDocumentToDelete(null)
    }

    return (
        <Section>
            <Modal
                open={modal}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{fontWeight: 600, mb: 2}}
                    >
                        Confirmar exclusão
                    </Typography>
                    <Typography
                        id="modal-modal-description"
                        sx={{mb: 3, color: '#5E5E5E'}}
                    >
                        Tem certeza que deseja excluir esta análise de
                        documento? Esta ação não pode ser desfeita.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCloseModal}
                            sx={{textTransform: 'none'}}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmModal}
                            sx={{textTransform: 'none'}}
                        >
                            Excluir
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={feedbackModal}
                onClose={handleCloseFeedbackModal}
                aria-labelledby="feedback-modal-title"
                aria-describedby="feedback-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 480,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        id="feedback-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{fontWeight: 600, mb: 2}}
                    >
                        Enviar feedback
                    </Typography>
                    <Typography
                        id="feedback-modal-description"
                        sx={{mb: 2, color: '#5E5E5E'}}
                    >
                        {feedbackTarget?.name}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Descreva sua análise sobre este documento..."
                        value={feedbackText}
                        onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                        ) => setFeedbackText(e.target.value)}
                        sx={{mb: 3}}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCloseFeedbackModal}
                            sx={{textTransform: 'none'}}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitFeedback}
                            disabled={
                                !feedbackText.trim() || feedbackSubmitting
                            }
                            sx={{textTransform: 'none'}}
                        >
                            {feedbackSubmitting ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Snackbar
                open={feedbackToast.open}
                autoHideDuration={4000}
                onClose={() =>
                    setFeedbackToast((prev: typeof feedbackToast) => ({
                        ...prev,
                        open: false,
                    }))
                }
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    severity={feedbackToast.severity}
                    onClose={() =>
                        setFeedbackToast((prev) => ({...prev, open: false}))
                    }
                >
                    {feedbackToast.message}
                </Alert>
            </Snackbar>
            <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a', mb: 0.5}}>
                Análise de Documentos
            </Typography>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                Ferramenta inteligente para análise e processamento de documentos
            </Typography>
            <Body>
                {/* ─────────────── Regime jurídico da análise ─────────────── */}
                <div
                    style={{
                        width: 'calc(100% - 40px)',
                        margin: '20px 20px 4px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <label
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#858585',
                            paddingBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        REGIME JURÍDICO
                        <Tooltip
                            title="Define a base legal da análise. Selecione a Lei nº 14.133/2021 para a Administração Pública em geral, ou a Lei nº 13.303/2016 (Lei das Estatais) para empresas públicas e sociedades de economia mista."
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '400px',
                                        fontSize: '13px',
                                        padding: '12px 16px',
                                        backgroundColor: '#333',
                                        color: '#fff',
                                    },
                                },
                            }}
                        >
                            <InfoIcon
                                style={{color: '#DFDFDF'}}
                                fontSize="small"
                            />
                        </Tooltip>
                    </label>
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                        }}
                    >
                        {REGIMES.map((r) => {
                            const active = regime === r.value
                            return (
                                <button
                                    key={r.value}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => setRegime(r.value)}
                                    style={{
                                        flex: '1 1 260px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '14px',
                                        padding: '16px 18px',
                                        borderRadius: '12px',
                                        border: `1.5px solid ${
                                            active ? r.accent : '#e4e4e4'
                                        }`,
                                        backgroundColor: active
                                            ? r.soft
                                            : '#fff',
                                        boxShadow: active
                                            ? `0 4px 14px ${r.soft}`
                                            : '0 1px 2px rgba(0,0,0,0.04)',
                                        transition: 'all 0.18s ease',
                                    }}
                                >
                                    <span
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            border: `2px solid ${
                                                active ? r.accent : '#c4c4c4'
                                            }`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {active && (
                                            <span
                                                style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: r.accent,
                                                }}
                                            />
                                        )}
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '15px',
                                                color: active
                                                    ? r.accent
                                                    : '#2b2b2b',
                                            }}
                                        >
                                            {r.lei}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: '12.5px',
                                                color: '#6b6b6b',
                                            }}
                                        >
                                            {r.label} · {r.sublabel}
                                        </span>
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                    <span
                        style={{
                            marginTop: '10px',
                            fontSize: '12.5px',
                            color: '#8a8a8a',
                            lineHeight: 1.5,
                        }}
                    >
                        {selectedRegime.desc}
                    </span>
                    {regime === '13303' &&
                        visibleDocumentTypes.length === 0 && (
                            <div
                                style={{
                                    marginTop: '12px',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(31,141,122,0.08)',
                                    border: '1px solid rgba(31,141,122,0.25)',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}
                            >
                                <InfoIcon
                                    fontSize="small"
                                    style={{color: '#1F8D7A', marginTop: '1px'}}
                                />
                                <span
                                    style={{
                                        fontSize: '13.5px',
                                        color: '#3a3a3a',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    As minutas-gabarito da{' '}
                                    <strong>
                                        Lei das Estatais (Lei nº 13.303/2016)
                                    </strong>{' '}
                                    estão sendo cadastradas. Assim que
                                    disponíveis, os tipos de documento aparecerão
                                    aqui automaticamente.
                                </span>
                            </div>
                        )}
                </div>
                <div
                    style={{
                        width: 'calc(100% - 40px)',
                        margin: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <label
                        style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#858585',
                            paddingBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        TIPO DO DOCUMENTO
                        <Tooltip
                            title='Selecione o tipo principal do documento enviado. Se você está enviando o edital com seus anexos consolidados em um arquivo único, selecione "Edital (com ou sem anexos)". A análise considerará automaticamente o conteúdo dos anexos integrados.'
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: '400px',
                                        fontSize: '13px',
                                        padding: '12px 16px',
                                        backgroundColor: '#333',
                                        color: '#fff',
                                    },
                                },
                            }}
                        >
                            <InfoIcon
                                style={{color: '#DFDFDF'}}
                                fontSize="small"
                            />
                        </Tooltip>
                    </label>
                    <FormControl
                        fullWidth
                        variant="outlined"
                        sx={{height: '60px'}}
                    >
                        <Select
                            // Deriva o value do tipo selecionado (que tem fallback p/
                            // um tipo visível), evitando um value órfão fora das opções
                            // no intervalo até o backend responder (warning do MUI).
                            value={selectedDocumentType?.apiId ?? ''}
                            onChange={(e) =>
                                setSelectedDocType(e.target.value as string)
                            }
                            sx={{
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {visibleDocumentTypes.map((item) => {
                                return (
                                    <MenuItem
                                        key={item.apiId}
                                        value={item.apiId}
                                        sx={{height: '60px'}}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: item.soft,
                                                    borderRadius: 8,
                                                    border: `1px solid ${item.accent}`,
                                                }}
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    style={{
                                                        color: item.accent,
                                                    }}
                                                >
                                                    <path d={item.iconPath} />
                                                </svg>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <span style={{fontWeight: 500}}>
                                                    {item.name}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#6b6b6b',
                                                    }}
                                                >
                                                    {item.desc}
                                                </span>
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '16px',
                        marginLeft: '20px',
                        marginRight: '20px',
                        padding: '10px 12px',
                        backgroundColor: '#f0f4ff',
                        borderRadius: '6px',
                        color: '#555',
                    }}
                >
                    <span
                        style={{
                            fontSize: '14px',
                            lineHeight: '1.5',
                            color: '#666',
                        }}
                    >
                        ⓘ A análise processa apenas um arquivo por vez. Para
                        obter o melhor resultado, consolide o edital e todos os
                        seus anexos (TR, minuta de contrato, ETP, planilhas) em
                        um único PDF antes do upload.
                    </span>
                </div>
                {showAlertFileSize && (
                    <Alert
                        severity="error"
                        onClose={() => {
                            setShowAlertFileSize(false)
                        }}
                        style={{marginBottom: '16px'}}
                    >
                        O tamanho máximo permitido por arquivo é 20 MB.
                    </Alert>
                )}
                {pdfAlertMessage && (
                    <Alert
                        severity="info"
                        onClose={() => {
                            setPdfAlertMessage('')
                        }}
                        style={{marginBottom: '16px'}}
                    >
                        {pdfAlertMessage}
                    </Alert>
                )}
                {loadingList ? (
                    <div style={{width: '100%'}}>
                        <Skeleton
                            variant="rounded"
                            height={200}
                            style={{
                                margin: '20px',
                                borderRadius: '16px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        {Array.from({length: 3}).map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={120}
                                style={{
                                    marginBottom: '16px',
                                    borderRadius: '16px',
                                    backgroundColor: '#f0f0f0',
                                }}
                            />
                        ))}
                    </div>
                ) : DocAnalysisList.length > 0 || isLoading ? (
                    <>
                        {isLoading ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        padding: '16px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        backgroundColor: '#fafafa',
                                    }}
                                >
                                    <img
                                        src={
                                            progress >= 100
                                                ? aiIcon
                                                : getFileIcon(file?.name)
                                        }
                                        alt="Arquivo"
                                        style={{width: '63px', height: '63px'}}
                                    />
                                    <div
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            style={{fontWeight: 500}}
                                        >
                                            {file
                                                ? file.name
                                                : 'Carregando arquivo...'}
                                        </Typography>
                                        {progress < 100 ? (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {file
                                                            ? `${(
                                                                  (file.size /
                                                                      1024 /
                                                                      1024) *
                                                                  (progress /
                                                                      100)
                                                              ).toFixed(
                                                                  2
                                                              )} MB / ${(
                                                                  file.size /
                                                                  1024 /
                                                                  1024
                                                              ).toFixed(2)} MB`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >{`${Math.round(
                                                        progress
                                                    )}%`}</Typography>
                                                </div>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{
                                                        borderRadius: '27px',
                                                        height: '10px',
                                                        marginTop: '4px',
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <Typography
                                                component="span"
                                                style={{
                                                    marginTop: '8px',
                                                    color: '#5E5E5E',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                <CircularProgress
                                                    size={18}
                                                    thickness={5}
                                                    sx={{
                                                        color: '#5E5E5E',
                                                        marginRight: '8px',
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                                {loadingMessage}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: 'calc(100% - 40px)',
                                    height: '200px',
                                    backgroundColor: selectedDocBg || '#f5f5f5',
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '20px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault()
                                    if (
                                        e.dataTransfer.files &&
                                        e.dataTransfer.files.length > 0
                                    ) {
                                        const file = e.dataTransfer.files[0]
                                        e.dataTransfer.clearData()

                                        if (!validateFileSize(file)) {
                                            return
                                        }

                                        handleAnalyzeProject(file)
                                    }
                                }}
                                onClick={() => {
                                    const input =
                                        document.createElement('input')
                                    input.type = 'file'
                                    input.accept = '.pdf,.docx'
                                    input.multiple = false
                                    input.onchange = (e) => {
                                        const files = (
                                            e.target as HTMLInputElement
                                        ).files
                                        if (files && files.length > 0) {
                                            const file = files[0]

                                            if (!validateFileSize(file)) {
                                                return
                                            }

                                            handleAnalyzeProject(file)
                                        }
                                    }
                                    input.click()
                                }}
                            >
                                <div
                                    style={{
                                        textAlign: 'center',
                                        color: '#292D32',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{marginBottom: '8px'}}
                                    >
                                        Arraste e solte seu{' '}
                                        {selectedDocumentType?.name ??
                                            'arquivo'}{' '}
                                        aqui
                                    </Typography>
                                    <Typography color="#5E5E5E" variant="body2">
                                        Arquivos suportados: PDF e DOCX
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        style={{
                                            margin: '8px 0',
                                            backgroundColor: '#E1E1E1',
                                            color: '#848484',
                                        }}
                                    >
                                        Escolher arquivo
                                    </Button>
                                    <Typography color="#5E5E5E" variant="body2">
                                        Tamanho Máximo: 20MB
                                    </Typography>
                                </div>
                            </div>
                        )}
                        {DocAnalysisList.map((arquivo, arquivoIndex) => {
                            const items = analyzedItems[arquivoIndex]
                            const isLoadingDetails =
                                detailsLoading[arquivoIndex]
                            // Resolve o badge contra TODOS os tipos (DOC_TYPES), não
                            // só os visíveis no regime atual — senão, ao trocar o
                            // regime no seletor, análises do outro regime perderiam o
                            // badge (o histórico mistura análises dos dois regimes).
                            const documentTypeBadge =
                                DOC_TYPES.find(
                                    (item) =>
                                        item.apiId === arquivo.document_type_id
                                ) || null

                            return (
                                <Accordion
                                    key={arquivoIndex}
                                    expanded={expandedIndex === arquivoIndex}
                                    onChange={handleAccordionChange(
                                        arquivoIndex,
                                        arquivo.doc_analysis_uuid
                                    )}
                                    sx={{
                                        borderRadius: '16px',
                                        marginBottom: '16px',
                                        boxShadow: 'none',
                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        '&.MuiAccordion-root': {
                                            borderRadius: '16px',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`panel${arquivoIndex}-content`}
                                        id={`panel${arquivoIndex}-header`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                width: '100%',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <img
                                                src={getFileIcon(
                                                    arquivo.file_url
                                                )}
                                                alt="Arquivo"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                }}
                                            />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={arquivo.file_url}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: '8px',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    cursor: 'pointer',
                                                                },
                                                            }}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation()

                                                                const res =
                                                                    api.downloadAnalyzedItem(
                                                                        arquivo.doc_analysis_uuid,
                                                                        arquivo.file_url
                                                                    )
                                                                console.log(
                                                                    'Iniciando download de:',
                                                                    res
                                                                )
                                                            }}
                                                        >
                                                            {
                                                                arquivo.doc_analysis_name
                                                            }
                                                            {documentTypeBadge && (
                                                                <DocumentTypeBadge
                                                                    docType={
                                                                        documentTypeBadge
                                                                    }
                                                                    sx={{
                                                                        marginLeft:
                                                                            '8px',
                                                                    }}
                                                                />
                                                            )}
                                                            <DownloadForOfflineOutlinedIcon fontSize="small" />
                                                        </Typography>
                                                    </Tooltip>
                                                    <Tooltip title={'Excluir'}>
                                                        <IconButton
                                                            sx={{
                                                                color: 'red',
                                                                marginLeft:
                                                                    '8px',
                                                            }}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation()
                                                                setDocumentToDelete(
                                                                    arquivo.doc_analysis_uuid
                                                                )
                                                                setModal(true)
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={
                                                            isLoadingDetails
                                                                ? 'Carregando dados da análise...'
                                                                : 'Baixar análise em PDF'
                                                        }
                                                    >
                                                        <IconButton
                                                            disabled={Boolean(
                                                                isLoadingDetails
                                                            )}
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                handleDownloadAnalysisPDF(
                                                                    event,
                                                                    arquivoIndex,
                                                                    arquivo.doc_analysis_uuid,
                                                                    arquivo.file_url
                                                                )
                                                            }}
                                                        >
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Enviar feedback sobre esta análise">
                                                        <IconButton
                                                            onClick={(
                                                                event: React.MouseEvent<HTMLButtonElement>
                                                            ) => {
                                                                event.stopPropagation()
                                                                handleOpenFeedbackModal(
                                                                    arquivo.doc_analysis_uuid,
                                                                    arquivo.doc_analysis_name
                                                                )
                                                            }}
                                                        >
                                                            <RateReviewOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            justifyContent:
                                                                'center',
                                                            alignItems:
                                                                'center',
                                                            marginLeft: 'auto',
                                                            marginRight: '16px',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#2eb936',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '0'
                                                            ] || 0}
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#ffc421',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '1'
                                                            ] || 0}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    '#dc2c2c',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                color: 'white',
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            {arquivo
                                                                .status_data?.[
                                                                '2'
                                                            ] || 0}
                                                        </Box>
                                                    </Box>
                                                </div>

                                                <Typography
                                                    component="span"
                                                    style={{
                                                        color: '#5E5E5E',
                                                        fontWeight: 400,
                                                        fontSize: '14px',
                                                        lineHeight: '180%',
                                                        letterSpacing: '0px',
                                                    }}
                                                >
                                                    {arquivo.created_at
                                                        ? new Date(
                                                              arquivo.created_at
                                                          ).toLocaleString(
                                                              'pt-BR',
                                                              {
                                                                  day: '2-digit',
                                                                  month: '2-digit',
                                                                  year: 'numeric',
                                                                  hour: '2-digit',
                                                                  minute: '2-digit',
                                                              }
                                                          )
                                                        : '-'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {isLoadingDetails && (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{padding: '8px 0'}}
                                            >
                                                Carregando detalhes...
                                            </Typography>
                                        )}

                                        {!isLoadingDetails &&
                                            items &&
                                            items.length > 0 && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    {items.map((item) => {
                                                        const {
                                                            icon,
                                                            label,
                                                            color,
                                                        } = getStatusVisuals(
                                                            item.status
                                                        )

                                                        return (
                                                            <div
                                                                key={
                                                                    item.analyzed_item_uuid
                                                                }
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    gap: '12px',
                                                                    padding:
                                                                        '12px',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius:
                                                                        '12px',
                                                                    backgroundColor:
                                                                        '#fafafa',
                                                                }}
                                                            >
                                                                <img
                                                                    src={icon}
                                                                    alt={label}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        flex: 1,
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        gap: '4px',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color,
                                                                        }}
                                                                    >
                                                                        {item.analyzed_item_title ||
                                                                            label}
                                                                    </Typography>
                                                                    <AnalyzedItemContent>
                                                                        <Markdown
                                                                            remarkPlugins={[
                                                                                remarkGfm,
                                                                            ]}
                                                                        >
                                                                            {
                                                                                item.content
                                                                            }
                                                                        </Markdown>
                                                                    </AnalyzedItemContent>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}

                                        {!isLoadingDetails &&
                                            (!items || items.length === 0) && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{padding: '8px 0'}}
                                                >
                                                    Documento em processo de
                                                    análise.
                                                </Typography>
                                            )}
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                        <Pagination
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            page={page}
                            onChange={(_event, value: number) => {
                                setPage(value)
                                setAnalyzedItems([])
                                setExpandedIndex(null)
                            }}
                        />
                    </>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',

                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                width: 'calc(100% - 40px)',
                                height: '200px',
                                backgroundColor: selectedDocBg || '#f5f5f5',
                                border: '2px dashed #ccc',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '20px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault()
                                if (
                                    e.dataTransfer.files &&
                                    e.dataTransfer.files.length > 0
                                ) {
                                    const file = e.dataTransfer.files[0]
                                    e.dataTransfer.clearData()

                                    if (!validateFileSize(file)) {
                                        return
                                    }

                                    handleAnalyzeProject(file)
                                }
                            }}
                            onClick={() => {
                                const input = document.createElement('input')
                                input.type = 'file'
                                input.accept = '.pdf,.docx'
                                input.multiple = false
                                input.onchange = (e) => {
                                    const files = (e.target as HTMLInputElement)
                                        .files
                                    if (files && files.length > 0) {
                                        const file = files[0]

                                        if (!validateFileSize(file)) {
                                            return
                                        }

                                        handleAnalyzeProject(file)
                                    }
                                }
                                input.click()
                            }}
                        >
                            <div
                                style={{
                                    textAlign: 'center',
                                    color: '#292D32',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    style={{marginBottom: '8px'}}
                                >
                                    Arraste e solte seu{' '}
                                    {selectedDocumentType?.name ?? 'arquivo'}{' '}
                                    aqui
                                </Typography>
                                <Typography color="#5E5E5E" variant="body2">
                                    Arquivos suportados: PDF e DOCX
                                </Typography>
                                <Button
                                    variant="contained"
                                    style={{
                                        margin: '8px 0',
                                        backgroundColor: '#E1E1E1',
                                        color: '#848484',
                                    }}
                                >
                                    Escolher arquivo
                                </Button>
                                <Typography color="#5E5E5E" variant="body2">
                                    Tamanho Máximo: 20MB
                                </Typography>
                            </div>
                        </div>
                        <Typography
                            variant="h6"
                            style={{
                                marginBottom: '20px',
                                color: 'rgb(102, 102, 102)',
                            }}
                        >
                            Nenhum documento analisado ainda.
                        </Typography>
                        <img
                            src={empty}
                            alt="Nenhum edital"
                            style={{width: '507px', marginBottom: '20px'}}
                        />
                    </div>
                )}
            </Body>
            <Typography
                style={{
                    width: '50%',
                }}
                variant="body2"
                sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#5E5E5E',
                }}
            >
                Esta análise é gerada por inteligência artificial e pode conter
                alertas imprecisos (falsos positivos) ou deixar de identificar
                problemas existentes. Os resultados não substituem a revisão por
                profissional qualificado.
            </Typography>

        </Section>
    )
}
